import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import HeroSection from './components/HeroSection';
import CategoryDropdown from './components/CategoryDropdown';
import CategoryGrid from './components/CategoryGrid';
import ContentSection from './components/ContentSection';
import FaqSection from './components/FaqSection';
import Footer from './components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL;

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'CSS Spreadsheet Hub',
  url: siteUrl,
  description: 'The most complete CSSBuy Spreadsheet database with shopping guides and agent comparisons.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteUrl}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is a CSSBuy spreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A CSSBuy spreadsheet is a curated collection of product links, typically organized in Google Sheets or Excel format, that helps shoppers browse and purchase items from Chinese marketplaces through the CSSBuy agent platform. These spreadsheets include direct links to products on Taobao, Weidian, and 1688, along with pricing information, size charts, and quality assessments.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is a Kakobuy spreadsheet better than CSSBuy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'It depends on what you are looking for. Kakobuy spreadsheets tend to focus on budget-friendly finds with competitive shipping rates, while CSSBuy spreadsheets often feature a broader range of products with more detailed QC photos. Many experienced shoppers maintain spreadsheets for both platforms and choose based on the specific item type and shipping destination.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which agent has the cheapest spreadsheet items?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The underlying product prices are generally the same across agents since they all source from the same Chinese marketplaces. The real cost differences come from agent service fees, shipping rates, and conversion fees. CSSBuy typically offers lower service fees, while Superbuy provides more comprehensive quality inspection services that may justify the slightly higher cost for expensive items.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are Superbuy spreadsheets reliable?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, Superbuy spreadsheets are generally considered reliable, especially for higher-value purchases. Superbuy has a strong reputation for quality control photography and responsive customer service. Their spreadsheet community tends to focus on premium streetwear and sneaker categories where detailed inspection matters most.',
      },
    },
    {
      '@type': 'Question',
      name: 'How often are spreadsheets updated?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Quality spreadsheets are typically updated weekly or bi-weekly. Dead links are removed, new finds are added, and seasonal trend sections are refreshed. The best CSSBuy sheet maintainers update their databases at least once a week to ensure listings remain active and pricing stays current.',
      },
    },
  ],
};

export default function Home() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />
      <main>
        <HeroSection />

        <section id="categories" className="w-full py-12 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-3">
                Shop by Category
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                Browse our extensive collection of verified spreadsheet finds across every product category. Each link takes you directly to kaspreadsheet.com.
              </p>
            </div>

            <div className="mb-8 md:mb-10">
              <CategoryDropdown />
            </div>

            <CategoryGrid />
          </div>
        </section>

        <section className="w-full py-12 md:py-16 relative overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.06]"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=Light%20airy%20fashion%20boutique%20interior%20with%20wooden%20shelves%20and%20neatly%20folded%20garments%2C%20warm%20ambient%20lighting%2C%20cream%20and%20natural%20wood%20tones%2C%20minimalist%20retail%20space%2C%20soft%20bokeh%20depth%20of%20field%2C%20elegant%20European%20boutique%20aesthetic%2C%20peaceful%20atmosphere&width=1600&height=600&seq=cta-bg-fashion-v2&orientation=landscape')`,
            }}
          />
          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-sparkling-line text-xs"></i>
              </div>
              <span>Most Popular</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-4">
              Ready to Start Shopping?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto mb-7">
              Access the full CSSBuy sheet database with thousands of verified product links, updated daily. Find sneakers, streetwear, accessories, and more.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap px-7 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-200"
              >
                CSSBuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap px-7 py-3.5 rounded-full bg-accent-500 text-white font-semibold text-sm hover:bg-accent-600 transition-all duration-200"
              >
                Buy It Now
              </a>
              <Link
                to="/article/cssbuy-spreadsheet-complete-guide"
                className="whitespace-nowrap px-7 py-3.5 rounded-full bg-background-50 border border-background-300/60 text-foreground-700 font-medium text-sm hover:bg-background-100 transition-all duration-200"
              >
                Read Guide
              </Link>
            </div>
          </div>
        </section>

        <ContentSection />
        <FaqSection />
      </main>
      <Footer />
    </>
  );
}